import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext, useLocation } from "react-router-dom";
import { usePatient } from "../../context/PatientContext";
import { useTheme } from "../../context/ThemeContext";
import PatientSymptomsForm from "../Forms/PatientSymptomsForm";
import { useAuth } from "../../context/AuthProvider";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

const EditPatientSymptoms = () => {
  const { patientData, setPatientData, selectedCase, selectedVisit, doctorInfo } = usePatient();
  const { theme } = useTheme();
  const { patientId, caseId, visitId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { markSectionComplete } = useOutletContext();
  const { verifyTokenAndRedirect } = useAuth();
  const location = useLocation();

  useEffect(() => {
    verifyTokenAndRedirect();
    if (!patientData && !patientId && !caseId && !visitId) {
      setPatientData({
        PATIENT_ID: null,
        CASE_ID: null,
        VISIT_ID: null,
        patientSymptoms: [{}],
      });
    }
  }, [verifyTokenAndRedirect, patientData, patientId, caseId, visitId, setPatientData]);

  const onSubmitHandler = (updatedSymptoms, options = {}) => {
    const isAutoSave = options?.isAutoSave;
    const updatedData = {
      ...patientData,
      patientSymptoms: patientData?.patientSymptoms?.length > 0
        ? patientData.patientSymptoms.map(info => ({
          ...info,
          ...updatedSymptoms,
          PATIENT_ID: patientData.PATIENT_ID,
          CASE_ID: selectedCase || caseId,
          VISIT_ID: selectedVisit || visitId,
          DOCTOR_ID: doctorInfo.MANDALI_ID,
          CLINIC_ID: doctorInfo.CLINIC_ID
        }))
        : [{
          ...updatedSymptoms,
          PATIENT_ID: patientData.PATIENT_ID,
          CASE_ID: selectedCase || caseId,
          VISIT_ID: selectedVisit || visitId,
          DOCTOR_ID: doctorInfo.MANDALI_ID,
          CLINIC_ID: doctorInfo.CLINIC_ID
        }]
    };

    setPatientData(updatedData);
    markSectionComplete("symptoms");
    if (!isAutoSave) {
      if (location.pathname.startsWith("/addpatient")) {
        navigate("/addpatient/history");
      } else if (location.pathname.startsWith("/visits")) {
        navigate(`/visits/${patientId}/${caseId}/${visitId}/history`);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="animate-spin mb-4" size={40} style={{ color: theme.primary }} />
        <p className="font-medium opacity-60" style={{ color: theme.text }}>Loading symptoms...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-10">
        <h3
          className="text-2xl md:text-4xl mb-3 tracking-tight"
          style={{
            fontFamily: theme.fonts?.serif,
            color: theme.text
          }}
        >
          Present Symptoms
        </h3>
        <div className="h-1 w-12 rounded-full mb-6" style={{ backgroundColor: theme.primary }} />
        <p className="text-sm md:text-base opacity-60 font-medium" style={{ color: theme.textSecondary }}>
          Recording the current health concerns and physical manifestations.
        </p>
      </div>

      {patientData || (patientId === undefined && caseId === undefined && visitId === undefined) ? (
        <PatientSymptomsForm
          initialValues={{
            ...patientData,
            patientSymptoms: patientData?.patientSymptoms || [{}]
          }}
          onSubmit={onSubmitHandler}
          onAutoSave={(values) => onSubmitHandler(values, { isAutoSave: true })}
          enableReinitialize={true}
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="animate-spin mb-4" size={32} style={{ color: theme.primary }} />
          <p className="opacity-60">Initializing form data...</p>
        </div>
      )}
    </div>
  );
};

export default EditPatientSymptoms;
